<template>

  <modalo
    :title="integration.label || 'WhatsApp Business'"
    type="mini"
    @hideModal="close()">

    <div class="whatsapp-connect">
      <p>{{ $t('whatsapp_connect_intro') }}</p>
      <ol class="pl-3">
        <li>{{ $t('whatsapp_connect_step_1') }}</li>
        <li>{{ $t('whatsapp_connect_step_2') }}</li>
        <li>{{ $t('whatsapp_connect_step_3') }}</li>
      </ol>
    </div>

    <div v-if="loading">
      <div class="d-flex align-items-center justify-content-center my-5">
        <div class="mr-3">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
        <div>
          <i>Não feche esta janela. O processo de conexão pode levar alguns minutos.</i>
        </div>
      </div>
    </div>
    <div v-else>
      <div
        v-if="errorMessage"
        class="alert alert-warning mt-3">
        {{ errorMessage }}
      </div>
    </div>

    <template slot="footer">
      <div :class="{'box-loading': loading}">
        <button
          type="button"
          class="btn btn-light mr-2"
          :disabled="loading"
          @click="close()">{{ $t('Cancel') }}</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="loading || !sdkReady"
          @click="connect()">{{ $t('Connect') }}</button>
      </div>
    </template>

  </modalo>

</template>

<script>
import config from '@/config'

const FB_SDK_ID = 'facebook-jssdk'
const FB_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js'

export default {
  name: 'WhatsappConnect',
  props: {
    integration: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      sdkReady: false,
      errorMessage: null,
      // session info captured from the embedded signup message events
      sessionInfo: {
        waba_id: null,
        phone_number_id: null
      }
    }
  },
  mounted () {
    window.addEventListener('message', this.onMessage)
    this.loadSdk()
  },
  beforeDestroy () {
    window.removeEventListener('message', this.onMessage)
  },
  methods: {
    /*
    loadSdk
    asynchronously loads the facebook javascript sdk and initializes it with
    the app id / graph version from the config.
    */
    loadSdk () {
      // already initialized (e.g. modal reopened)
      if (window.FB) {
        this.sdkReady = true
        return
      }

      window.fbAsyncInit = () => {
        window.FB.init({
          appId: config.meta.app_id,
          cookie: true,
          xfbml: true,
          version: config.meta.graph_version
        })
        this.sdkReady = true
      }

      // inject the sdk script once
      const first = document.getElementsByTagName('script')[0]
      if (document.getElementById(FB_SDK_ID)) {
        return
      }
      const js = document.createElement('script')
      js.id = FB_SDK_ID
      js.src = FB_SDK_SRC
      first.parentNode.insertBefore(js, first)
    },
    /*
    onMessage
    listens for the messages meta posts during the embedded signup flow,
    capturing the waba_id and phone_number_id of the connected account.
    */
    onMessage (event) {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') {
        return
      }

      let payload
      try {
        payload = JSON.parse(event.data)
      } catch (e) {
        console.error('onMessage: ignoring non-json message', event.data)
        // messages that are not json (unrelated postMessage traffic)
        return
      }

      if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') {
        return
      }

      if (payload.event === 'FINISH' || payload.event === 'FINISH_ONLY_WABA' || payload.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
        this.sessionInfo.waba_id = payload.data && payload.data.waba_id
        this.sessionInfo.phone_number_id = payload.data && payload.data.phone_number_id
      } else if (payload.event === 'CANCEL') {
        this.loading = false
        this.errorMessage = this.$t('whatsapp_connect_cancelled')
      } else if (payload.event === 'ERROR') {
        this.loading = false
        this.errorMessage = (payload.data && payload.data.error_message) || this.$t('whatsapp_connect_error')
      }
    },
    /*
    connect
    launches the whatsapp embedded signup flow. on success the login callback
    returns an authorization code that, together with the waba_id /
    phone_number_id captured from the message events, is sent to the backend.
    */
    connect () {
      if (!this.sdkReady || !window.FB) {
        this.errorMessage = this.$t('whatsapp_connect_sdk_not_ready')
        return
      }

      this.loading = true
      this.errorMessage = null
      this.sessionInfo.waba_id = null
      this.sessionInfo.phone_number_id = null

      window.FB.login((response) => {
        const authResponse = response && response.authResponse
        if (!authResponse || !authResponse.code) {
          // user closed the dialog without authorizing
          this.loading = false
          if (!this.errorMessage) {
            this.errorMessage = this.$t('whatsapp_connect_cancelled')
          }
          return
        }

        this.createConnection(authResponse.code)
      }, {
        config_id: config.meta.whatsapp_config_id,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {
            // Optional: You can pre-fill business and phone data here
          },
          // featureType: 'whatsapp_embedded_signup', // signup normal, sem coexistência whastaap app + API
          featureType: 'whatsapp_business_app_onboarding', // ativa a coexistência whastaap app + API
          sessionInfoVersion: '3'
        }
      })
    },
    /*
    createConnection
    sends the embedded signup result to the backend.
    */
    createConnection (code) {
      this.$store
      .dispatch('createConnection', {
        integration: this.integration.integration,
        data: {
          code: code,
          waba_id: this.sessionInfo.waba_id,
          phone_number_id: this.sessionInfo.phone_number_id,
          is_sandbox: config.meta.is_sandbox
        }
      })
      .then((result) => {
        this.$emit('connected', result)
      })
      .catch((err) => {
        const data = err.response && err.response.data
        this.errorMessage = (data && data.message) || null
      })
      .finally(() => {
        this.loading = false
      })
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/Integrations/connectors/whatsapp/Connect.vue